import React from 'react';
import { useNavigate } from 'react-router';
import { ObjectStates as ObjectTypStates } from '../../../../common/src/enums/ObjectTypeStates';
import { useTranslate } from '../../mixins/translate/translate';
import { openApiFetchDelete, openApiFetchGet } from '../../store/openApiFetch';
import { Roles } from '../../utils/enums/Roles';
import { SortColumn, SortDirections } from '../../utils/sorting';
import { IconEditButton } from '../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../common/iconButtons/IconRemoveButton';
import {
    Content,
    EditPanelSection,
    EditPanelSelect,
    Panel,
    PanelBody,
    PanelButtonLink,
    PanelRouterLink,
    RightTray,
    Table,
    TableBody,
    TableButtons,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    TableSortableHeader,
    Topbar,
    TopbarCrumbActive,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../elements';
import { Spinner } from '../spinners';
import { useUserHasRole } from '../utils/authorization/useUserHasRole';
import { ListPager } from '../utils/ListPager';
import { useReactAsync } from '../utils/useReactAsync';
import { useSearch } from '../utils/useSearch';

interface ISearchAttributes {
    status?: string;
}

async function loadObjectTypes(searchParameters: ISearchAttributes & { limit: string; offset: string; sortColumns: SortColumn[] }) {
    const result = await openApiFetchGet('/api/objectTypes/', searchParameters);
    return result;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function ObjectTypesList() {
    const { userHasRole } = useUserHasRole();
    const isAdmin = userHasRole([Roles.Administrator]);
    const translate = useTranslate();
    const navigate = useNavigate();

    const { searchParameters, handleSortChanged, handleNavigation, setSearchAttribute, queryObject, itemsPerPage } = useSearch<ISearchAttributes>(
        {},
        [{ columnName: 'name', direction: SortDirections.Ascending }],
    );

    const { data, reload, isLoading } = useReactAsync(loadObjectTypes, queryObject, [queryObject]);

    async function removeObjectType(objectTypeId: string) {
        await openApiFetchDelete('/api/objectTypes/{objectTypeId}', { objectTypeId });
        await reload();
    }

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive title={translate('common.objectTypes')} icon="fa-cogs" />
                </TopbarLeft>
                <TopbarRight>
                    <PanelRouterLink
                        to="/settings/objecttypes/add"
                        text={translate('common.addItem', translate('common.objectType'))}
                        icon="fa fa-plus"
                        small={true}
                    />
                </TopbarRight>
            </Topbar>

            <Content>
                <Tray>
                    <Panel>
                        {(isLoading && <Spinner />) || (
                            <PanelBody noPadding={true}>
                                <Table>
                                    <TableHead>
                                        <TableHeadRow light={true}>
                                            <TableSortableHeader
                                                header={translate('common.name')}
                                                columnName="name"
                                                sortColumns={searchParameters.sortColumns}
                                                onClick={handleSortChanged}
                                            />
                                            <TableSortableHeader
                                                header={translate('common.language')}
                                                columnName="language.name"
                                                sortColumns={searchParameters.sortColumns}
                                                onClick={handleSortChanged}
                                            />
                                            {isAdmin && (
                                                <TableSortableHeader
                                                    header={translate('common.company')}
                                                    columnName="clientCompany.name"
                                                    sortColumns={searchParameters.sortColumns}
                                                    onClick={handleSortChanged}
                                                />
                                            )}
                                            <TableHeader>&nbsp;</TableHeader>
                                        </TableHeadRow>
                                    </TableHead>
                                    <TableBody>
                                        {data &&
                                            data.items.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableData> {item.name} </TableData>
                                                    <TableData> {item.language.name} </TableData>
                                                    {isAdmin && (
                                                        <>
                                                            <TableData>{item.clientCompany.name}</TableData>
                                                        </>
                                                    )}
                                                    <TableButtons large>
                                                        <PanelButtonLink
                                                            text={translate('objectTypeField.fields')}
                                                            xsmall
                                                            onClick={() => navigate(`/settings/objecttypes/${item.id}/objectTypeFields`)}
                                                        />
                                                        {isAdmin && (
                                                            <PanelButtonLink
                                                                text={translate('common.composition')}
                                                                xsmall
                                                                onClick={() => navigate(`/settings/objecttypes/${item.id}/inspectionstandards`)}
                                                            />
                                                        )}
                                                        {(isAdmin || item.clientCompany.name) && (
                                                            <IconEditButton onClick={() => navigate(`/settings/objecttypes/${item.id}/edit`)} />
                                                        )}
                                                        {(isAdmin || item.clientCompany.name) && item.status !== ObjectTypStates.Deleted && (
                                                            <IconRemoveButton
                                                                removeQuestion={translate('common.confirmDelete', item.name)}
                                                                onClick={async () => removeObjectType(item.id)}
                                                            />
                                                        )}
                                                    </TableButtons>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </PanelBody>
                        )}
                        <ListPager
                            disabled={isLoading}
                            itemsPerPage={itemsPerPage}
                            total={data?.total ?? 0}
                            page={searchParameters.page}
                            handleNavigation={handleNavigation}
                        />
                    </Panel>
                </Tray>

                {isAdmin && (
                    <RightTray>
                        <form
                            id="filter"
                            onSubmit={(e) => {
                                e.preventDefault();
                            }}
                        >
                            <div className="admin-form">
                                <h4> {translate('common.findItem', translate('common.objectType'))}</h4>
                                <EditPanelSection>
                                    <EditPanelSelect
                                        name="objectTypeStatus"
                                        value={searchParameters.status}
                                        handleChange={(value) => setSearchAttribute({ status: value })}
                                        disabled={isLoading}
                                        placeholder={translate('common.selectItem', translate('common.status'))}
                                        options={[
                                            { value: ObjectTypStates.Active, text: translate(`status.${ObjectTypStates.Active}`) },
                                            { value: ObjectTypStates.Deleted, text: translate(`status.${ObjectTypStates.Deleted}`) },
                                        ]}
                                    />
                                </EditPanelSection>
                            </div>
                        </form>
                    </RightTray>
                )}
            </Content>
        </>
    );
}
